import { useEffect } from "react";

/**
 * Lightweight SEO head manager. Sets <title>, meta description, canonical,
 * OG tags and JSON-LD schema blocks without external deps.
 */
export default function Seo({ title, description, path = "", image, schema }) {
  useEffect(() => {
    if (title) document.title = title;

    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "website", true);
    if (image) setMeta("og:image", image, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    const origin = window.location.origin;
    const canonical = `${origin}${path || window.location.pathname}`;
    setLink("canonical", canonical);
    setMeta("og:url", canonical, true);

    // Schema
    const existing = document.querySelectorAll('script[data-cps-schema="true"]');
    existing.forEach((n) => n.remove());
    const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
    schemas.forEach((obj) => {
      if (!obj) return;
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.cpsSchema = "true";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, path, image, JSON.stringify(schema)]);

  return null;
}

function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Helpers
export function localBusinessSchema(company, options = {}) {
  const { reviews, ratingCount } = options;
  const base = {
    "@context": "https://schema.org",
    "@type": "HousePainter",
    "@id": `${window.location.origin}/#business`,
    name: company.name,
    image: `${window.location.origin}/og-image.jpg`,
    telephone: company.phone,
    email: company.email,
    areaServed: [
      { "@type": "City", name: "Sunshine Coast", containedInPlace: { "@type": "State", name: "Queensland" } },
      { "@type": "City", name: "Maroochydore" },
      { "@type": "City", name: "Mooloolaba" },
      { "@type": "City", name: "Buderim" },
      { "@type": "City", name: "Noosa" },
      { "@type": "City", name: "Caloundra" },
      { "@type": "City", name: "Coolum Beach" },
      { "@type": "City", name: "Peregian Beach" },
      { "@type": "City", name: "Kawana" },
      { "@type": "City", name: "Alexandra Headland" },
      { "@type": "City", name: "Bokarina" },
      { "@type": "City", name: "Birtinya" },
      { "@type": "City", name: "Mountain Creek" },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: company.address.locality,
      addressRegion: company.address.region,
      postalCode: company.address.postal,
      addressCountry: company.address.country,
    },
    url: window.location.origin,
    priceRange: "$$$",
    openingHours: "Mo,Tu,We,Th,Fr,Sa 07:00-17:00",
    identifier: [
      company.abn ? { "@type": "PropertyValue", propertyID: "ABN", value: company.abn } : null,
      company.qbcc ? { "@type": "PropertyValue", propertyID: "QBCC Licence", value: company.qbcc } : null,
    ].filter(Boolean),
  };

  // Embed reviews & rating INTO the primary HousePainter entity (avoids duplicate business schema)
  if (Array.isArray(reviews) && reviews.length > 0) {
    base.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: ratingCount || reviews.length,
      bestRating: 5,
    };
    base.review = reviews.slice(0, 6).map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
      reviewBody: t.text,
    }));
  }

  return base;
}

export function serviceSchema({ name, description, url, areaServed = "Sunshine Coast, QLD" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    name,
    description,
    url,
    areaServed: { "@type": "Place", name: areaServed },
    provider: { "@type": "HousePainter", "@id": `${window.location.origin}/#business`, name: "Connolly's Painting Solutions", url: window.location.origin },
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
